import React, { useEffect, useState } from 'react';
import { getSubscription, getUsage, createPaymentIntent, confirmSubscription, openPortal } from '../../api/billing';
import { PaymentModal } from './PaymentForm';
import { analytics } from '../../services/analytics';

interface Plan {
  id: string;
  name: string;
  price: string;
  minutes: number;
  features: string[];
  current?: boolean;
}

const PLANS: Plan[] = [
  { id: 'free', name: 'Free', price: '$0', minutes: 50, features: ['1 Agent', '1 Phone Number', '50 min/month'] },
  { id: 'starter', name: 'Starter', price: '$29', minutes: 500, features: ['3 Agents', '3 Phone Numbers', '500 min/month', 'Email support'] },
  { id: 'growth', name: 'Growth', price: '$99', minutes: 2000, features: ['10 Agents', '10 Phone Numbers', '2,000 min/month', 'Priority support', 'Campaigns'] },
  { id: 'pro', name: 'Pro', price: '$299', minutes: 10000, features: ['Unlimited Agents', 'Unlimited Numbers', '10,000 min/month', 'Dedicated support', 'Campaigns', 'API access'] },
];

export const Billing: React.FC = () => {
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [minutesUsed, setMinutesUsed] = useState(0);
  const [minutesTotal, setMinutesTotal] = useState(50);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    clientSecret: string;
    plan: string;
    planName: string;
    planPrice: string;
    intentType: 'setup' | 'payment';
  }>({ open: false, clientSecret: '', plan: '', planName: '', planPrice: '', intentType: 'setup' });

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const [sub, usage] = await Promise.all([
          getSubscription().catch(() => null),
          getUsage().catch(() => null),
        ]);
        if (sub?.plan) setCurrentPlan(sub.plan);
        if (usage?.minutes_used != null) setMinutesUsed(usage.minutes_used);
        if (usage?.minutes_total != null) setMinutesTotal(usage.minutes_total);
      } catch {
        // fallback to defaults
      } finally {
        setLoading(false);
      }
    };
    fetchBilling();
  }, []);

  const handleUpgrade = async (planId: string) => {
    setUpgrading(planId);
    analytics.track('pricing_viewed', { planId });
    try {
      const res = await createPaymentIntent(planId);
      const planInfo = PLANS.find((p) => p.id === planId);
      analytics.track('checkout_started', { plan: planId, price: planInfo?.price });
      setPaymentModal({
        open: true,
        clientSecret: res.clientSecret,
        plan: planId,
        planName: planInfo?.name || planId,
        planPrice: planInfo?.price || '',
        intentType: res.type,
      });
    } catch {
      // ignore
    } finally {
      setUpgrading(null);
    }
  };

  const handlePaymentSuccess = async (intentId: string) => {
    try {
      if (paymentModal.intentType === 'setup') {
        await confirmSubscription(paymentModal.plan, intentId);
      }
      // Refresh subscription data
      const sub = await getSubscription().catch(() => null);
      if (sub?.plan) setCurrentPlan(sub.plan);
    } catch {
      // ignore
    } finally {
      setPaymentModal((prev) => ({ ...prev, open: false }));
    }
  };

  const handleManage = async () => {
    try {
      const res = await openPortal();
      if (res?.url) window.location.href = res.url;
    } catch {
      // ignore
    }
  };

  const usagePercent = minutesTotal > 0 ? Math.min((minutesUsed / minutesTotal) * 100, 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading billing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Billing</h1>
        <p className="text-gray-400 mt-1">Manage your subscription and usage.</p>
      </div>

      {/* Usage card */}
      <div
        className="rounded-xl p-4 md:p-6 mb-8"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Minutes Usage</h2>
            <p className="text-gray-400 text-sm mt-0.5">
              Current plan: <span className="text-indigo-400 font-medium capitalize">{currentPlan}</span>
            </p>
          </div>
          {currentPlan !== 'free' && (
            <button
              onClick={handleManage}
              className="text-sm px-4 py-2 rounded-lg transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#d1d5db' }}
            >
              Manage Subscription
            </button>
          )}
        </div>

        <div className="flex items-end gap-2 mb-2">
          <span className="text-3xl font-bold text-white">{minutesUsed.toLocaleString()}</span>
          <span className="text-gray-400 text-sm mb-1">/ {minutesTotal.toLocaleString()} minutes</span>
        </div>

        <div className="w-full h-3 rounded-full mb-1" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${usagePercent}%`,
              background: usagePercent > 90 ? '#f87171' : usagePercent > 70 ? '#fbbf24' : '#6366f1',
            }}
          />
        </div>
        <p className="text-xs text-gray-500">{Math.round(usagePercent)}% used this billing cycle</p>
      </div>

      {/* Plans grid */}
      <h2 className="text-lg font-semibold text-white mb-5">Plans</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          return (
            <div
              key={plan.id}
              className="rounded-xl p-5 flex flex-col"
              style={{
                background: '#111118',
                border: isCurrent ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {isCurrent && (
                <span className="inline-flex self-start items-center px-2 py-0.5 rounded-full text-xs font-medium mb-3" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
                  Current plan
                </span>
              )}
              <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
              <div className="mt-2 mb-4">
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-gray-400 text-sm">/month</span>
              </div>
              <ul className="space-y-2 flex-1 mb-5">
                {plan.features.map((f) => (
                  <li key={f} className="text-sm text-gray-400 flex items-center gap-2">
                    <span className="text-indigo-400 text-xs">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <button
                  disabled
                  className="w-full py-2.5 rounded-lg text-sm font-medium"
                  style={{ background: 'rgba(255,255,255,0.05)', color: '#6b7280', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  Current
                </button>
              ) : plan.id === 'free' ? null : (
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={upgrading === plan.id}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-white transition-all"
                  style={{ background: '#6366f1', border: '1px solid rgba(99,102,241,0.5)' }}
                >
                  {upgrading === plan.id ? 'Loading...' : 'Upgrade'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Billing history placeholder */}
      <div
        className="rounded-xl p-4 md:p-6"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h2 className="text-lg font-semibold text-white mb-4">Billing History</h2>
        <p className="text-gray-500 text-sm">No billing history yet. Your invoices will appear here.</p>
      </div>

      {/* Payment Modal — embedded, no redirect */}
      <PaymentModal
        open={paymentModal.open}
        clientSecret={paymentModal.clientSecret}
        plan={paymentModal.plan}
        planName={paymentModal.planName}
        planPrice={paymentModal.planPrice}
        intentType={paymentModal.intentType}
        onSuccess={handlePaymentSuccess}
        onClose={() => setPaymentModal((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};
